/**
 * Super-Admin → Server Tools page.
 *
 * Currently exposes one operation: install (or re-install) LibreOffice
 * so the office-document → PDF preview pipeline can render .docx /
 * .xlsx / .pptx evidence inside our own trust boundary.
 *
 * Backed by:
 *   GET  /api/admin/server-tools/office-converter/status
 *   POST /api/admin/server-tools/office-converter/install
 *
 * Both endpoints are super-admin only (server-side gate via
 * db.super_admins). The frontend ALSO checks ``user.is_super_admin``
 * on mount so non-admins who somehow land on the route get a clean
 * 403-style message instead of a confusing API error.
 *
 * Why a dedicated page rather than a modal on the dashboard?
 *  - The install can take ~30-90s on a fresh container (apt-get
 *    fetching ~300 MB). A modal would lock the user; a dedicated page
 *    gives room for a status panel + stdout/stderr surface for
 *    troubleshooting.
 *  - Easier to extend later when we add more server tools (e.g. cache
 *    flush, queue health) without refactoring the dashboard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  ArrowLeft, Loader2, FileText, CheckCircle2, AlertTriangle, RefreshCw, Terminal,
} from 'lucide-react';

const API = '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const ServerTools = () => {
  const navigate = useNavigate();
  // Super-admin detection follows the SAME contract as App.js' PrivateRoute
  // (line ~403): `currentWorkspace.role === 'super_admin'`. The user object
  // doesn't carry an `is_super_admin` flag — super-admin status lives on
  // the active workspace context that gets restored after emulation. Using
  // the wrong field here is what produced the "Super-admin access required"
  // false-positive on production for sri@theinceptica.com.
  const { currentWorkspace } = useAuth() || {};
  const isSuper = currentWorkspace?.role === 'super_admin';

  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installResult, setInstallResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/server-tools/office-converter/status`, authHeaders()
      );
      setStatus(data);
    } catch (e) {
      // 403 is expected if a non-admin nav-typed this URL — show a friendly hint.
      const code = e?.response?.status;
      toast.error(
        code === 403
          ? 'Super-admin access required.'
          : `Failed to load status: ${e?.response?.data?.detail || e.message}`
      );
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    if (isSuper) fetchStatus();
  }, [isSuper, fetchStatus]);

  const runInstall = async () => {
    setInstalling(true);
    setInstallResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/server-tools/office-converter/install`, null, authHeaders()
      );
      setInstallResult(data);
      // ``status`` is one of: already_installed | installed | failed.
      // The success toast text mirrors that so the operator knows
      // exactly what happened without reading the diagnostics box.
      if (data.status === 'installed') {
        toast.success('LibreOffice installed — office previews now work.');
      } else if (data.status === 'already_installed') {
        toast.info('LibreOffice is already installed on this host.');
      } else {
        toast.error('Install reported a failure — check the diagnostics below.');
      }
      await fetchStatus();
    } catch (e) {
      // Backend wraps the diagnostics envelope inside ``detail`` on
      // failure so we still get stdout / stderr to render.
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setInstallResult({ ...detail, status: detail.status || 'failed' });
      }
      toast.error(
        typeof detail === 'string'
          ? detail
          : `Install failed: ${e?.response?.statusText || e.message}`
      );
    } finally {
      setInstalling(false);
    }
  };

  if (!isSuper) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Card className="border-red-200 bg-red-50/40" data-testid="server-tools-forbidden">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <CardTitle className="text-red-900">Super-admin access required</CardTitle>
            </div>
            <CardDescription className="text-red-800">
              Only platform super-admins can run server-level tools.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/admin')} data-testid="server-tools-back-403">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Super Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const available = !!status?.available;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="server-tools-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="server-tools-back"
          >
            <ArrowLeft className="w-4 h-4" /> Super Admin
          </button>
          <h1 className="text-2xl font-semibold text-slate-900 mt-1">Server Tools</h1>
          <p className="text-sm text-slate-600">
            Operational toggles that affect the platform host. Use sparingly — every
            action is audit-logged.
          </p>
        </div>
      </div>

      {/* ── Office document preview installer ───────────────────────── */}
      <Card className="shadow-sm" data-testid="office-converter-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                <CardTitle>Office Document Preview (LibreOffice)</CardTitle>
              </div>
              <CardDescription className="mt-1">
                Lets uploaded .doc, .docx, .xls, .xlsx, .ppt, .pptx evidence
                files render in-app as PDFs. Conversion runs entirely on
                Nova's infrastructure — customer evidence never reaches
                any third-party viewer.
              </CardDescription>
            </div>
            {loadingStatus ? (
              <Badge variant="outline" className="border-slate-300 text-slate-600">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Checking
              </Badge>
            ) : available ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"
                     data-testid="office-converter-status-available">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Installed
              </Badge>
            ) : (
              <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50"
                     data-testid="office-converter-status-missing">
                <AlertTriangle className="w-3 h-3 mr-1" /> Not installed
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Live state block — visible regardless of whether the user
              has clicked install yet. */}
          <div className="rounded border border-slate-200 bg-slate-50/60 px-3 py-2 text-[12px] space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-500">Binary</span>
              <span className="font-mono text-slate-800">
                {status?.binary || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Version</span>
              <span className="font-mono text-slate-800">
                {status?.version
                  ? status.version.split('\n')[0]
                  : (available ? 'Installed' : 'Not installed')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Install script</span>
              <span className="font-mono text-slate-700 truncate max-w-[60%]">
                {status?.install_script || '—'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              onClick={runInstall}
              disabled={installing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="office-converter-install-btn"
            >
              {installing
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Running…</>
                : available
                  ? <><RefreshCw className="w-4 h-4 mr-1" /> Re-run install (idempotent)</>
                  : 'Install LibreOffice'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={fetchStatus}
              disabled={loadingStatus || installing}
              data-testid="office-converter-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingStatus ? 'animate-spin' : ''}`} />
              Refresh status
            </Button>
            {installing && (
              <span className="text-[11px] text-slate-500 italic" data-testid="office-converter-install-hint">
                First-time install downloads ~300 MB and can take 30–90 s.
              </span>
            )}
          </div>

          {/* Diagnostics surface — only after an install attempt. Keeps
              stdout / stderr visible so an operator can troubleshoot
              without SSH access. */}
          {installResult && (
            <div className="rounded border border-slate-200 bg-slate-50/40 px-3 py-2"
                 data-testid="office-converter-result">
              <div className="flex items-center gap-2 mb-1">
                <Terminal className="w-4 h-4 text-slate-500" />
                <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                  Install output
                </span>
                <Badge variant="outline" className={
                  installResult.status === 'failed'
                    ? 'border-red-300 text-red-700 bg-red-50'
                    : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                } data-testid={`office-converter-result-status-${installResult.status}`}>
                  {installResult.status}
                </Badge>
                <span className="text-[11px] text-slate-500">
                  {installResult.duration_seconds != null && `${installResult.duration_seconds}s`}
                  {installResult.returncode != null && ` · exit ${installResult.returncode}`}
                </span>
              </div>
              {installResult.stdout && (
                <pre className="text-[11px] bg-slate-900 text-emerald-200 rounded p-2 overflow-x-auto max-h-48 whitespace-pre-wrap"
                     data-testid="office-converter-stdout">
                  {installResult.stdout}
                </pre>
              )}
              {installResult.stderr && (
                <>
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-red-600 mt-2 mb-1">
                    stderr
                  </div>
                  <pre className="text-[11px] bg-slate-900 text-red-300 rounded p-2 overflow-x-auto max-h-48 whitespace-pre-wrap"
                       data-testid="office-converter-stderr">
                    {installResult.stderr}
                  </pre>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ServerTools;
